// A small key-value store for the v2 client: IndexedDB when the browser has
// it, memory when it doesn't (a private window, blocked site data, tests).
// Every call is wrapped: a failing store costs the offline copy, never the app.

const DB = 'aithlo-v2';
const STORE = 'kv';

export function memoryPersist() {
  const m = new Map();
  return {
    get: async (k) => (m.has(k) ? structuredClone(m.get(k)) : undefined),
    set: async (k, v) => { m.set(k, structuredClone(v)); },
    del: async (k) => { m.delete(k); },
  };
}

function open(idb) {
  return new Promise((resolve, reject) => {
    const req = idb.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(req?.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export function browserPersist(idb = globalThis.indexedDB) {
  const fallback = memoryPersist();
  if (!idb) return fallback;
  let dbp = null;
  const db = () => { dbp = dbp || open(idb); return dbp; };
  const guard = (fn, fb) => async (...args) => {
    try { return await fn(await db(), ...args); } catch { return fb(...args); }
  };
  return {
    get: guard((d, k) => tx(d, 'readonly', (s) => s.get(k)), fallback.get),
    set: guard((d, k, v) => tx(d, 'readwrite', (s) => s.put(v, k)), fallback.set),
    del: guard((d, k) => tx(d, 'readwrite', (s) => s.delete(k)), fallback.del),
  };
}
